/**
 * Coloration syntaxique d'un code, moteur chargé à la demande.
 *
 * Le moteur pèse quelques dizaines de kilooctets : il n'est téléchargé qu'au
 * premier écran qui affiche du code, jamais par la liste d'applications.
 *
 * Le repli est **explicite plutôt que muet**. Sans coloration, un utilisateur ne
 * peut pas distinguer un texte trop volumineux d'un chargement en échec ou d'une
 * fonctionnalité absente : le motif est donc retourné, à charge pour l'appelant de
 * le dire.
 */

import { useEffect, useMemo, useState } from 'react';

import {
  loadHighlighter,
  MAX_HIGHLIGHT_CHARACTERS,
  type HighlightLanguage,
  type Highlighter,
  type HighlightResult,
} from '../lib/highlight';

/** Pourquoi la coloration n'est pas appliquée. */
export type HighlightUnavailable = 'chargement' | 'trop-volumineux' | 'indisponible';

export interface HighlightState {
  /** Résultat, ou `null` tant que la coloration n'est pas applicable. */
  result: HighlightResult | null;
  /** Motif du repli, `null` quand la coloration est bien appliquée. */
  unavailable: HighlightUnavailable | null;
}

/**
 * Colorise un code.
 *
 * @param code contenu à colorer
 * @param language langage imposé ; deviné à défaut
 */
export function useHighlightedCode(code: string, language?: HighlightLanguage): HighlightState {
  const [engine, setEngine] = useState<Highlighter | null>(null);
  const [failed, setFailed] = useState(false);

  const tooLarge = code.length > MAX_HIGHLIGHT_CHARACTERS;

  useEffect(() => {
    if (tooLarge) return undefined;

    let annule = false;
    loadHighlighter().then(
      (moteur) => {
        if (!annule) setEngine(moteur);
      },
      () => {
        // Morceau injoignable : la coloration est un confort, elle ne doit pas
        // empêcher de lire ni de modifier son code.
        if (!annule) setFailed(true);
      },
    );
    return () => {
      annule = true;
    };
  }, [tooLarge]);

  const result = useMemo(
    () => (engine && !tooLarge && code.length > 0 ? engine.highlight(code, language) : null),
    [engine, code, language, tooLarge],
  );

  if (tooLarge) return { result: null, unavailable: 'trop-volumineux' };
  if (failed) return { result: null, unavailable: 'indisponible' };
  if (result === null) return { result: null, unavailable: code.length > 0 ? 'chargement' : null };
  return { result, unavailable: null };
}
